import { Storage } from "aws-amplify";
import path from "path-browserify";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useImmer } from "use-immer";

import { assertNativeError } from "@examspark/common/dist/utils";
import { getUploadKeyForNewResource } from "@examspark/common/dist/utils/file";
import {
  getBucketName,
  getBucketRegion,
  useMutationCreateClassroomResource,
  useAppContext,
} from "@examspark/common-app";
import {
  Button,
  Dropzone,
  MiddleTruncate,
  ProgressBar,
} from "@examspark/common-ui";
import { IconTrashLine } from "@examspark/common-ui/dist/components/icons";
import { ResourceVisibilityType } from "@examspark/types";

import Modal, { ModalProps } from "../common/Modal";

const FilesPlaceholder = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  font-size: 1.3rem;
  color: #777;
`;
const FileItems = styled.div``;
const FileItem = styled.div`
  display: flex;
  padding: 1rem;
  border-top: solid 1px #ddd;
  align-items: center;
  gap: 1.5rem;

  &:first-child {
    border-top: none;
  }
`;
const FileItemName = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;
const FileItemProgress = styled.div`
  /* margin: 0 1rem; */
  width: 8rem;
`;
const FileItemActions = styled.div``;

interface FileUpload {
  file: File;
  type: string;
  status: string | null;
  progress: {
    total: number;
    loaded: number;
  };
  scale?: string;
}

const UploadFileModal = (
  props: ModalProps & {
    classroomId: string | undefined;
    onComplete?: () => void;
  },
) => {
  const [context] = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [files, updateFiles] = useImmer<FileUpload[]>([]);
  const browseButtonRef = useRef<HTMLInputElement>(null);
  const createResource = useMutationCreateClassroomResource();

  const handleUpload = async () => {
    try {
      setIsLoading(true);
      const workspaceId = context.workspaceUser?.id;
      console.log("files", files);
      if (!workspaceId) {
        throw new Error("No workspaceId");
      }

      await Promise.all(
        files.map(async (file, index) => {
          const fileInfo = path.parse(file.file.name);
          console.log("fileInfo", fileInfo);

          const fileKey = getUploadKeyForNewResource(file, workspaceId);
          console.log("fileKey", fileKey);

          const result = await Storage.put(fileKey.key, file.file, {
            progressCallback: (progress: { loaded: number; total: number }) => {
              console.log("progress", progress);
              updateFiles((draft) => {
                draft[index].progress = progress;
              });
            },
          });

          console.log("completed?", result);

          await createResource.mutateAsync({
            id: fileKey.resourceId,
            sk: "resource#v0",
            schemaVersion: "2020-01-01",
            visibility: ResourceVisibilityType.Private,
            workspaceId,
            creatorId: context.user?.id,
            type: "FILE",
            name: file.file.name,
            file: {
              bucket: getBucketName(),
              extension: fileInfo.ext,
              key: fileKey.key,
              mimetype: file.type,
              name: file.file.name,
              region: getBucketRegion(),
              size: file.file.size,
            },
          });

          updateFiles((draft) => {
            draft[index].status = "SUCCESS";
          });
        }),
      );

      setIsLoading(false);
      props.onComplete?.();
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsLoading(false);
      props.onComplete?.();
    }
  };

  const handleDrop = (acceptedFiles: File[]) => {
    acceptedFiles.forEach(async (file) => {
      updateFiles((draft) => {
        draft.push({
          type: "hello",
          status: "IN_PROGRESS",
          file,
          progress: {
            loaded: 0,
            total: file.size,
          },
        });
      });
    });
  };

  return (
    <Modal
      title="Upload File"
      onAfterClose={() => {
        setIsLoading(false);
        updateFiles(() => {
          return [];
        });
      }}
      buttons={
        <>
          <Button title="Cancel" onClick={props.onRequestClose}>
            Cancel
          </Button>
          <Button
            title="Browse"
            onClick={() => browseButtonRef.current?.click()}
          >
            Browse...
          </Button>
          <Button
            autoFocus
            disabled={isLoading || files.length === 0}
            isLoading={isLoading}
            color="primary"
            title="Upload Files"
            onClick={handleUpload}
          >
            Upload Files
          </Button>
        </>
      }
      {...props}
    >
      <Dropzone
        browseButtonRef={browseButtonRef}
        onDrop={(accepted) => {
          handleDrop(accepted);
        }}
      >
        <FileItems>
          {files.length === 0 && (
            <FilesPlaceholder>No Files Selected</FilesPlaceholder>
          )}
          {files.map((file, index) => {
            return (
              <FileItem key={index}>
                <FileItemName>
                  <MiddleTruncate text={file.file.name} />
                </FileItemName>
                {file.status === "SUCCESS" && <></>}
                <FileItemProgress>
                  <ProgressBar
                    loaded={file.progress?.loaded}
                    total={file.progress?.total}
                  />
                </FileItemProgress>
                <FileItemActions>
                  <Button
                    title="Remove"
                    icon={<IconTrashLine />}
                    onClick={() => {
                      updateFiles((draft) => {
                        draft.splice(index, 1);
                      });
                    }}
                  />
                </FileItemActions>
              </FileItem>
            );
          })}
        </FileItems>
      </Dropzone>
    </Modal>
  );
};

export default UploadFileModal;
